import { shouldImpersonate } from '@sdk/authentication/impersonate';
import getFeatureFlagClient from 'sling-sdk/src/featureFlags/launchdarkly';
import { LdFeatureFlags } from 'sling-sdk/src/featureFlags/types';

import config from '../config';
import { PREMIUM_FEATURE_NAMES } from '../constants';

import getSlingAppUrl from './get-sling-app.html';

export default function MenuCtrl(
  Authentication,
  WhatsNewService,
  PremiumDiscoveryService,
  SlingAnalytics,
  WindowLocation,
  $scope,
  $window,
  $document,
  $injector,
  $state,
  $uibModal
) {
  'ngInject';

  const controller = this;

  // Private variables
  let _featureFlagClient = null;
  let _createOrgEnabledCache = null;

  // Controller properties and methods
  controller.isAuthenticated = isAuthenticated;
  controller.isActive = isActive;
  controller.toggleSubmenu = toggleSubmenu;
  controller.switchOrg = switchOrg;
  controller.reload = reload;
  controller.getSlingApp = getSlingApp;
  controller.getBadge = getBadge;
  controller.openWhatsNew = openWhatsNew;
  controller.getWhatsNewBadge = getWhatsNewBadge;
  controller.goToTasks = goToTasks;
  controller.canShowTasks = canShowTasks;
  controller.SLING_ADMIN_URL = config.SLING_ADMIN_URL;
  controller.impersonated = shouldImpersonate();

  controller.goToBilling = () => {
    $state.go('account.overview.billing');

    const { viewPlansClicked } = SlingAnalytics.analyticsEvents;
    SlingAnalytics.track(viewPlansClicked, null);
  };

  // Initialize feature flags
  _initializeFeatureFlags();
  Object.defineProperty(controller, 'createOrgEnabled', {
    get() {
      if (_createOrgEnabledCache === null) {
        _createOrgEnabledCache = controller._checkFeatureFlag(LdFeatureFlags.CREATE_ORG_ENABLED, false);
      }
      return _createOrgEnabledCache;
    },
  });

  Object.defineProperty(controller, 'user', {
    get() {
      return Authentication.user;
    },
  });

  Object.defineProperty(controller, 'trialBannerVisible', {
    get() {
      return (
        Authentication.user &&
        Authentication.user.isManager && // show only to a manager or admin
        !Authentication.user.org.subscriptionStartDate && // show only if premium hasn't been purchased
        !Authentication.user.org.subscriptionCancelDate
      ); // show only if premium hasn't been purchased and then cancelled
    },
  });

  Object.defineProperty(controller, 'discoveryPopupVisible', {
    get() {
      return config.PREMIUM_DISCOVERY_ENABLED && PremiumDiscoveryService.isPopupVisible();
    },
  });

  Object.defineProperty(controller, 'isWhatsNewUnread', {
    get() {
      return WhatsNewService.isNewUnreadItem;
    },
  });

  Object.defineProperty(controller, 'isChristmasTheme', {
    get() {
      return (
        $window._IS_CHRISTMAS_THEME &&
        !Authentication.user?.settings.clientFlags.disableChristmasTheme
      );
    },
  });

  function isAuthenticated(sref) {
    if (!sref) {
      return false;
    }

    const access = $state.get(sref).data.access;
    return Authentication.authorize(access);
  }

  function isActive(sref, notIncludes) {
    return $state.includes(sref) && (notIncludes ? !$state.includes(notIncludes) : true);
  }

  function toggleSubmenu(event) {
    controller.openSubmenu = !controller.openSubmenu;
    if (controller.openSubmenu) {
      $document.one('click', () => {
        $scope.$apply(() => {
          controller.openSubmenu = false;
        });
      });

      // prevent immediate closing
      event.stopPropagation();
    }
  }

  function switchOrg(org) {
    if (org.id !== Authentication.user.org.id) {
      Authentication.switchOrg(org);
    }
  }

  function reload() {
    WindowLocation.reload();
  }

  function getSlingApp() {
    $uibModal
      .open({
        templateUrl: getSlingAppUrl,
        size: '620',
      })
      .result.ignoreCancel();
  }

  function getBadge(number) {
    return number > 99 ? '99+' : number;
  }

  function openWhatsNew() {
    WhatsNewService.openSidebar();
  }

  function getWhatsNewBadge() {
    if (WhatsNewService.data && Array.isArray(WhatsNewService.data)) {
      const whatsNewReadItems =
        Authentication.user.settings.clientFlags?.whatsNewReadItems.split(',') || [];

      return WhatsNewService.data.filter((item) => !whatsNewReadItems.includes(String(item.id)))
        .length;
    }

    return 0;
  }

  function canShowTasks() {
    const { org, orgSettings, isManager } = Authentication.user;
    if (orgSettings.tasksFeatureDisabled) {
      return false;
    }

    if (isManager) {
      return true;
    }

    const shiftTasksEnabled = Authentication.isOrgFeatureEnabled(PREMIUM_FEATURE_NAMES.shiftTasks);
    const oldTasksEnabled = config.OLD_TASKS_ENABLED && org.oldTasksUsed;

    return shiftTasksEnabled || oldTasksEnabled;
  }

  const stateMap = {
    oldTasks: 'tasks',
    shiftTasks: 'shiftTasks.instances',
    taskTemplates: 'shiftTasks.templates',
  };
  function goToTasks($event) {
    $event.preventDefault();
    const ShiftTasksTabsService = $injector.get('ShiftTasksTabsService');
    const { value: state } = ShiftTasksTabsService.currentTab;
    $state.go(stateMap[state]);
  }

  async function _initializeFeatureFlags() {
    try {
      _featureFlagClient = await getFeatureFlagClient();
      _createOrgEnabledCache = null;
    } catch (error) {
      console.error('Failed to initialize LaunchDarkly client:', error);
      _featureFlagClient = null;
    }
  }

  controller._checkFeatureFlag = function(flag, defaultValue = false) {
    try {
      if (_featureFlagClient && _featureFlagClient.isFeatureEnabled) {
        return _featureFlagClient.isFeatureEnabled(flag, defaultValue);
      }
      return defaultValue;
    } catch (error) {
      console.error('Error checking feature flag:', flag, error);
      return defaultValue;
    }
  };
}
